import { FC, useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Grid, Typography, CircularProgress, Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { CloudDownload, Check, Delete, Visibility, Edit, BarChart, Comment, Email, Help } from '@mui/icons-material';

import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import Table from '../../../common/components/Table/Table';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import CommentModal from '../../../common/components/CommentModal';
import CommentWithTable, { DatasetInfo } from '../../../common/components/CommentWithTable';
import ContactModal from '../../../common/components/ContactModal';
import { useNavItems, usePermission } from '../../../hooks';
import { useUser } from '../../../hooks/useUser';
import { UserRoles } from '../../../common/constants/userConstants';
import {
  useE53Data,
  useE53Download,
  useE53Finalize,
  useE53Reject as useE53DeleteFile,
  useE53AcceptFile,
  useE53Comments,
  useE53SubmitComment,
  E53Comment,
  E53CommentsResponse,
  TableType,
  StateType,
  SortConfig,
  E53DataFilterConfig as FilterConfig,
} from './hooks';
import type { e53File } from './types';

import { StyledContainer, MainContent, TableSection } from './e53Message.styled';
import { SortDirection, ContextMenuAction } from '../../../common/components/Table/types';
import { getE53Columns } from './columns/e53Columns';

const e53Message: FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const navItems = useNavItems();
  const { hasPermission, isLoading: permissionLoading } = usePermission('/e53', '');
  const { data: userData, isLoading: userLoading } = useUser();

  // State to track which tables to display based on filter
  const [tableView, setTableView] = useState<'all' | 'inbound' | 'outbound'>('all');
  // Add state to track status filter
  const [statusView, setStatusView] = useState<'all' | 'pending' | 'completed'>('all');

  // State for filters from URL parameters
  const [yearFilter, setYearFilter] = useState<string | undefined>(undefined);
  const [ppoFilter, setPpoFilter] = useState<string | undefined>(undefined);

  // Helper function to parse multiple sorts from URL
  const parseSortsFromURL = useCallback((params: URLSearchParams, prefix: string): SortConfig[] => {
    const sortParams = params.getAll(`${prefix}Sort`);
    const parsedSorts: SortConfig[] = [];
    
    sortParams.forEach((sortParam) => {
      const [column, direction] = sortParam.split(',');
      if (column && (direction === 'ASC' || direction === 'DESC')) {
        parsedSorts.push({ column, direction: direction as 'ASC' | 'DESC' });
      }
    });
    
    return parsedSorts;
  }, []);

  // Helper function to update URL with multiple sorts
  const updateSortsInURL = useCallback((newSorts: SortConfig[], params: URLSearchParams, prefix: string) => {
    // Remove all existing sort parameters for this table
    params.delete(`${prefix}Sort`);
    
    // Add new sort parameters
    newSorts.forEach((sort) => {
      params.append(`${prefix}Sort`, `${sort.column},${sort.direction}`);
    });
  }, []);

  // Helper function to parse multiple filters from URL
  const parseFiltersFromURL = useCallback((params: URLSearchParams, prefix: string): FilterConfig[] => {
    const filters: FilterConfig[] = [];
    // Get all filter parameters for this table
    const filterKeys = Array.from(params.keys()).filter(key => 
      key.startsWith(`${prefix}Filter`) && key.endsWith('Col')
    );
    
    filterKeys.forEach(key => {
      const colSuffix = key.replace(`${prefix}Filter`, '').replace('Col', '');
      const colValue = params.get(key);
      const termKey = `${prefix}Filter${colSuffix}Term`;
      const termValue = params.get(termKey);
      
      if (colValue && termValue) {
        filters.push({ column: colValue, term: termValue });
      }
    });
    
    // Also check for legacy single filter format
    const legacyCol = params.get(`${prefix}SearchCol`);
    const legacyTerm = params.get(`${prefix}SearchTerm`);
    if (legacyCol && legacyTerm && !filters.some(f => f.column === legacyCol)) {
      filters.push({ column: legacyCol, term: legacyTerm });
    }
    
    return filters;
  }, []);

  // Helper function to update URL with multiple filters
  const updateFiltersInURL = useCallback((newFilters: FilterConfig[], params: URLSearchParams, prefix: string) => {
    // Remove all existing filter parameters for this table
    const filterKeys = Array.from(params.keys()).filter(key => 
      key.startsWith(`${prefix}Filter`) || key === `${prefix}SearchCol` || key === `${prefix}SearchTerm`
    );
    filterKeys.forEach(key => params.delete(key));
    
    // Add new filter parameters
    newFilters.forEach((filter, index) => {
      params.set(`${prefix}Filter${index}Col`, filter.column);
      params.set(`${prefix}Filter${index}Term`, filter.term);
    });
  }, []);

  // State for all data table
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [pendingSorts, setPendingSorts] = useState<SortConfig[]>([]);
  const [pendingFilters, setPendingFilters] = useState<FilterConfig[]>([]);
  // Legacy search state for pending table (for backward compatibility)
  const [searchColumn, setSearchColumn] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);

  // State for inbound data table
  const [inboundPage, setInboundPage] = useState<number>(0);
  const [inboundPageSize, setInboundPageSize] = useState<number>(10);
  const [inboundSorts, setInboundSorts] = useState<SortConfig[]>([]);
  const [inboundFilters, setInboundFilters] = useState<FilterConfig[]>([]);
  // Legacy search state for inbound table
  const [inboundSearchColumn, setInboundSearchColumn] = useState<string | undefined>(undefined);
  const [inboundSearchTerm, setInboundSearchTerm] = useState<string | undefined>(undefined);

  // State for outbound data table
  const [outboundPage, setOutboundPage] = useState<number>(0);
  const [outboundPageSize, setOutboundPageSize] = useState<number>(10);
  const [outboundSorts, setOutboundSorts] = useState<SortConfig[]>([]);
  const [outboundFilters, setOutboundFilters] = useState<FilterConfig[]>([]);
  // Legacy search state for outbound table
  const [outboundSearchColumn, setOutboundSearchColumn] = useState<string | undefined>(undefined);
  const [outboundSearchTerm, setOutboundSearchTerm] = useState<string | undefined>(undefined);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Add state for confirmation dialogs
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmAction: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmAction: () => {},
  });

  // State for tracking deletion operation type
  const [deletionOperationType, setDeletionOperationType] = useState<string | null>(null);

  // Add state for comment modal
  const [commentModal, setCommentModal] = useState<{
    isOpen: boolean;
    fileId: number | null;
    isMarkingForRemoval: boolean;
  }>({
    isOpen: false,
    fileId: null,
    isMarkingForRemoval: false,
  });

  // State for CommentWithTable modal
  const [commentWithTableModal, setCommentWithTableModal] = useState<{
    isOpen: boolean;
    selectedFile: e53File | null;
    tableData: E53Comment[];
  }>({
    isOpen: false,
    selectedFile: null,
    tableData: [],
  });

  // State for Contact modal
  const [contactModal, setContactModal] = useState<{
    isOpen: boolean;
    selectedFile: e53File | null;
  }>({
    isOpen: false,
    selectedFile: null,
  });

  // Fetch comments for the selected file in CommentWithTable modal
  const { data: commentsData, isLoading: isCommentsLoading } = useE53Comments({
    dataSetId: Number(commentWithTableModal.selectedFile?.fileId) || 0,
    enabled: commentWithTableModal.isOpen && !!commentWithTableModal.selectedFile?.fileId
  });

  // Get operator PPO from filter or fallback to current user's designatedOperator
  // Memoize to prevent unnecessary recalculations
  const operatorPpo = useMemo(() => {
    // When PPO filter is 'ALL', return undefined to indicate no filtering
    if (ppoFilter === 'ALL') {
      return undefined;
    }
    // Use the PPO filter if it exists, otherwise use current user's designated operator
    return ppoFilter;
  }, [ppoFilter, userData?.designatedOperator]);

  // Fetch pending e53 data (table=pending, state=pending)
  const pendingDesignatedOperator = ppoFilter === 'ALL' ? undefined : (ppoFilter);
  const {
    data: pendingData,
    isLoading: isPendingLoading,
    isError: isPendingError,
    refetch: refetchPendingData,
  } = useE53Data({
    page,
    size: pageSize,
    sorts: pendingSorts.length > 0 ? pendingSorts : undefined,
    filters: pendingFilters.length > 0 ? pendingFilters : undefined,
    table: 'pending' as TableType,
    designatedOperator: pendingDesignatedOperator,
    state: statusView === 'completed' ? 'completed' : statusView === 'pending' ? 'pending' : 'all' as StateType,
    accountingYear: yearFilter,
    searchColumn: searchColumn, // Legacy support
    searchTerm: searchTerm, // Legacy support
    enabled: (tableView === 'all' || statusView === 'pending'),
  });

  // Fetch inbound e53 data (table=inbound, state based on statusView)
  const {
    data: inboundData,
    isLoading: isInboundLoading,
    isError: isInboundError,
    refetch: refetchInboundData,
  } = useE53Data({
    page: inboundPage,
    size: inboundPageSize,
    sorts: inboundSorts.length > 0 ? inboundSorts : undefined,
    filters: inboundFilters.length > 0 ? inboundFilters : undefined,
    table: 'inbound' as TableType,
    designatedOperator: operatorPpo,
    state: statusView === 'completed' ? 'completed' : statusView === 'pending' ? 'pending' : 'all' as StateType,
    accountingYear: yearFilter,
    searchColumn: inboundSearchColumn, // Legacy support
    searchTerm: inboundSearchTerm, // Legacy support
    enabled: (tableView === 'all' || tableView === 'inbound'),
  });

  // Fetch outbound e53 data (table=outbound, state based on statusView)
  const {
    data: outboundData,
    isLoading: isOutboundLoading,
    isError: isOutboundError,
    refetch: refetchOutboundData,
  } = useE53Data({
    page: outboundPage,
    size: outboundPageSize,
    sorts: outboundSorts.length > 0 ? outboundSorts : undefined,
    filters: outboundFilters.length > 0 ? outboundFilters : undefined,
    table: 'outbound' as TableType,
    designatedOperator: operatorPpo,
    state: statusView === 'completed' ? 'completed' : statusView === 'pending' ? 'pending' : 'all' as StateType,
    accountingYear: yearFilter,
    searchColumn: outboundSearchColumn, // Legacy support
    searchTerm: outboundSearchTerm, // Legacy support
    enabled: (tableView === 'all' || tableView === 'outbound'),
  });

  // Effect to refresh all data when the component mounts or when dependent variables change
  useEffect(() => {
    // Refresh all data when the component mounts
    const refreshAllData = async () => {
      try {
        // Refresh the data based on current view
        if (tableView === 'all' || statusView === 'pending') {
          await refetchPendingData();
        }

        if (tableView === 'all' || tableView === 'inbound') {
          await refetchInboundData();
        }

        if (tableView === 'all' || tableView === 'outbound') {
          await refetchOutboundData();
        }
      } catch (error) {
        console.error('Error refreshing data:', error);
      }
    };

    refreshAllData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.designatedOperator, statusView, tableView]); // Re-run when user's designatedOperator, statusView or tableView changes

  // Download e53 file
  const { mutate: downloadFile, isPending: isDownloading } = useE53Download({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'File downloaded successfully',
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to download file',
        severity: 'error',
      });
    },
  });

  // Add finalize mutation
  const { mutate: finalizeFile, isPending: isFinalizing } = useE53Finalize();

  // Add accept file mutation
  const { mutate: acceptFile, isPending: isAccepting } = useE53AcceptFile();

  // Delete e53 file hook
  const deleteFile = useE53DeleteFile({
    onSuccess: () => {
      let successMessage = 'File deleted successfully';
      
      if (deletionOperationType === 'Mark File for Removal') {
        successMessage = 'File marked for removal successfully';
      } else if (deletionOperationType === 'Confirm Removal') {
        successMessage = 'File removed successfully';
      } else if (deletionOperationType === 'Undo Removal') {
        successMessage = 'Undid removal successfully';
      }
      
      setSnackbar({
        open: true,
        message: successMessage,
        severity: 'success',
      });
      
      // Reset operation type
      setDeletionOperationType(null);

      // Reload the page after a short delay to show the updated status
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    },
    onError: (error) => {
      const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : error instanceof Error ? error.message : 'Unknown error';
      setSnackbar({
        open: true,
        message: `${errorMessage}`,
        severity: 'error',
      });
    },
  });

  // Submit comment hook
  const submitComment = useE53SubmitComment({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Comment submitted successfully',
        severity: 'success',
      });
      handleCloseCommentWithTableModal();
      
      // Refetch table data based on current view
      const refreshTableData = async () => {
        try {
          if (tableView === 'all' || statusView === 'pending') {
            await refetchPendingData();
          }
          if (tableView === 'all' || tableView === 'inbound') {
            await refetchInboundData();
          }
          if (tableView === 'all' || tableView === 'outbound') {
            await refetchOutboundData();
          }
        } catch (error) {
          console.error('Error refreshing table data after comment submission:', error);
        }
      };
      
      refreshTableData();
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Failed to submit comment: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    },
  });

  // Handle successful acceptance
  const handleAcceptSuccess = () => {
    setSnackbar({
      open: true,
      message: 'File has been accepted successfully',
      severity: 'success',
    });
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };
  
  // Handle acceptance error
  const handleAcceptError = (error: unknown) => {
    setSnackbar({
      open: true,
      message: `Failed to accept file: ${error instanceof Error ? error.message : 'Unknown error'}`,
      severity: 'error',
    });
  };

  // Helper function to check module permissions
  const hasModulePermission = useCallback(
    (permissionName: string) => {
      if (!userData || !userData.modules) return false;

      const e53Module = userData.modules.find((module) => module.name === 'e53 Sampling');

      if (!e53Module || !e53Module.permissions) return false;

      return e53Module.permissions.some(
        (permission) => permission.name === permissionName && permission.enabled
      );
    },
    [userData]
  );

  // Helper function to validate file operations
  const validateFileOperation = useCallback(
    (row: e53File, operation: 'edit' | 'delete') => {
      // Extract relevant data from the row
      const isInboundAccepted = row.inboundAcceptanceDate && row.inboundAcceptanceDate !== '-';
      const isOutboundAccepted = row.outboundAcceptanceDate && row.outboundAcceptanceDate !== '-';
      const isPendingOnUser = row.pendingOn === userData?.designatedOperator;
      const isPendingOnOtherPPO = row.pendingOn && row.pendingOn !== userData?.designatedOperator;
      const isFullyAccepted = isInboundAccepted && isOutboundAccepted;
      const isDraft = !isFullyAccepted && (!row.pendingOn || row.pendingOn === null);
      const isMarkedForRemoval = (row as any).inboundRemovalDate !== null || (row as any).outboundRemovalDate !== null || row.removalDate !== null;
      const isFinal = row.fileStatus === 'F';

      // Custom validation based on operation type
      if (operation === 'edit') {
        // Scenario 1: File is pending on other PPO (not my DO)

        if (isMarkedForRemoval) {
          return {
            allowed: false,
            message: "Sorry you cannot edit this file because it is marked for removal"
          };
        }

        if (isPendingOnOtherPPO) {
          return {
            allowed: false,
            message: "Sorry you can't edit this file, only the other PPO can make amendments."
          };
        }
        
        // Scenario 2: File is fully accepted by both sides
        if (isFullyAccepted) {
          return {
            allowed: false,
            message: "This file has already been agreed on by both PPO's. No further editing is allowed."
          };
        }

        // Scenario 3: File is not pending on anyone (null) and not fully accepted
        if (isDraft) {
          return {
            allowed: false,
            message: "Sorry you can't edit this file, please make sure you selected the highest version."
          };
        }
        
        // Scenario 4: File is pending on the current user (allowed)
        if (isPendingOnUser) {
          return {
            allowed: true,
            message: ""
          };
        }
        
        // Default case
        return {
          allowed: false,
          message: "You don't have permission to edit this file."
        };
      } 
      
      if (operation === 'delete') {
        // Scenario 1: File is fully accepted by both sides
        if (isFullyAccepted) {
          // Check if user is admin
          const isAdmin = userData?.role === UserRoles.ADMIN;
          
          // If user is admin, allow deletion with admin delete confirmation
          if (isAdmin) {
            return {
              allowed: true,
              title: "Admin Delete",
              message: "Are you sure you want to delete this file?",
              requiresComment: false
            };
          }
          // If not admin, show error
          return {
            allowed: false,
            message: "You cannot delete a file which has already been accepted by both DOs."
          };
        }
        
        // Check if file is FINAL
        if (isFinal) {
          // If file is FINAL and already marked for removal
          if (isMarkedForRemoval) {
            // Check if removalDo is populated
            if (row.removalDo) {
              // Compare removalDo with current user's DO
              if (row.removalDo === userData?.designatedOperator) {
                // Same DO - ask if they want to undo removal
                return {
                  allowed: true,
                  title: "Undo Removal",
                  message: "Do you want to undo the removal of this file?",
                  requiresComment: false
                };
              } else {
                // Different DO - ask to confirm removal
                return {
                  allowed: true,
                  title: "Confirm Removal",
                  message: "Do you confirm removal of this file?",
                  requiresComment: false
                };
              }
            }
            // No removalPpo but has removal date - standard delete
            return {
              allowed: true,
              message: "Are you sure you want to delete this file?",
              requiresComment: false
            };
          }
          // If file is FINAL and not marked for removal, mark for removal with comment
          return {
            allowed: true,
            title: "Mark File for Removal",
            message: "Are you sure you want to mark this file for removal?",
            requiresComment: true
          };
        }
        
        // If file is not FINAL, delete without comment
        return {
          allowed: true,
          title: "Draft File Removal",
          message: "Are you sure you want to delete this file?",
          requiresComment: false
        };
      }
      
      // Default response for unknown operations
      return {
        allowed: false,
        message: "Operation not supported."
      };
    },
    [userData]
  );

  // Handlers for confirmation dialog
  const handleCloseConfirmDialog = () => {
    setConfirmDialog({
      isOpen: false,
      title: '',
      message: '',
      confirmAction: () => {},
    });
  };

  const handleConfirmAction = () => {
    // Execute the stored action
    confirmDialog.confirmAction();
    // Close the dialog
    handleCloseConfirmDialog();
  };

  // Helper function to update URL parameters while preserving existing ones
  const updateURLParams = useCallback(
    (newParams: Record<string, string | number | null>) => {
      const params = new URLSearchParams(searchParams);

      Object.entries(newParams).forEach(([key, value]) => {
        if (value === null) {
          params.delete(key);
        } else {
          params.set(key, String(value));
        }
      });

      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Function to determine row background color based on inbound/outbound
  const getRowBackgroundColor = useCallback((row: e53File): string => {
    // Check if operatorByInboundPpo equals current user's designated operator
    const isInbound = row.operatorByInboundPpo === userData?.designatedOperator;
    
    // Return green for inbound, red for outbound
    return isInbound ? '#e8f5e9' : '#ffebee';
  }, [userData?.designatedOperator]);

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/e53');
  };

  // Handlers for main table
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    updateURLParams({ pendingPage: newPage });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
    updateURLParams({ pendingPage: 0, pendingPageSize: newPageSize });
  };

  const handleSort = useCallback(
    (column: string, direction: SortDirection | undefined, searchColumn?: string, searchTerm?: string) => {
      setPage(0);
      
      // Handle multiple sorts for pending table
      let newSorts: SortConfig[] = [...pendingSorts];
      
      if (direction && direction !== 'undefined' as any) {
        // Check if this column is already in the sorts array
        const existingIndex = newSorts.findIndex((s) => s.column === column);
        
        if (existingIndex >= 0) {
          // Update existing sort direction
          newSorts[existingIndex].direction = direction;
        } else {
          // Add new sort
          newSorts.push({ column, direction });
        }
      } else {
        // Remove sort for this column if direction is undefined
        newSorts = newSorts.filter((s) => s.column !== column);
      }
      
      setPendingSorts(newSorts);
      
      // Handle multiple filters for pending table
      let newFilters: FilterConfig[] = [...pendingFilters];
      
      if (searchColumn !== undefined) {
        if (searchTerm && searchTerm.trim()) {
          // Check if this column already has a filter
          const existingFilterIndex = newFilters.findIndex((f) => f.column === searchColumn);
          
          if (existingFilterIndex >= 0) {
            // Update existing filter term
            newFilters[existingFilterIndex].term = searchTerm.trim();
          } else {
            // Add new filter
            newFilters.push({ column: searchColumn, term: searchTerm.trim() });
          }
        } else {
          // Remove filter for this column if searchTerm is empty or undefined
          newFilters = newFilters.filter((f) => f.column !== searchColumn);
        }
      }
      
      setPendingFilters(newFilters);
      
      // Update legacy state for backward compatibility
      setSearchColumn(searchColumn);
      setSearchTerm(searchTerm);
      
      const params = new URLSearchParams(searchParams);
      params.set('pendingPage', '0');
      
      // Update sorts in URL
      updateSortsInURL(newSorts, params, 'pending');
      
      // Update filters in URL
      updateFiltersInURL(newFilters, params, 'pending');
      
      setSearchParams(params);
    },
    [pendingSorts, pendingFilters, searchParams, setSearchParams, updateSortsInURL, updateFiltersInURL]
  );

  // Handlers for inbound table
  const handleInboundPageChange = (newPage: number) => {
    setInboundPage(newPage);
    updateURLParams({ inboundPage: newPage });
  };

  const handleInboundPageSizeChange = (newPageSize: number) => {
    setInboundPageSize(newPageSize);
    setInboundPage(0);
    updateURLParams({ inboundPage: 0, inboundPageSize: newPageSize });
  };

  const handleInboundSort = useCallback(
    (column: string, direction: SortDirection | undefined, searchColumn?: string, searchTerm?: string) => {
      setInboundPage(0);
      
      // Handle multiple sorts for inbound table
      let newSorts: SortConfig[] = [...inboundSorts];
      
      if (direction && direction !== 'undefined' as any) {
        // Check if this column is already in the sorts array
        const existingIndex = newSorts.findIndex((s) => s.column === column);
        
        if (existingIndex >= 0) {
          // Update existing sort direction
          newSorts[existingIndex].direction = direction;
        } else {
          // Add new sort
          newSorts.push({ column, direction });
        }
      } else {
        // Remove sort for this column if direction is undefined
        newSorts = newSorts.filter((s) => s.column !== column);
      }
      
      setInboundSorts(newSorts);
      
      // Handle multiple filters for inbound table
      let newFilters: FilterConfig[] = [...inboundFilters];
      
      if (searchColumn !== undefined) {
        if (searchTerm && searchTerm.trim()) {
          // Check if this column already has a filter
          const existingFilterIndex = newFilters.findIndex((f) => f.column === searchColumn);
          
          if (existingFilterIndex >= 0) {
            // Update existing filter term
            newFilters[existingFilterIndex].term = searchTerm.trim();
          } else {
            // Add new filter
            newFilters.push({ column: searchColumn, term: searchTerm.trim() });
          }
        } else {
          // Remove filter for this column if searchTerm is empty or undefined
          newFilters = newFilters.filter((f) => f.column !== searchColumn);
        }
      }
      
      setInboundFilters(newFilters);
      
      // Update legacy state for backward compatibility
      setInboundSearchColumn(searchColumn);
      setInboundSearchTerm(searchTerm);
      
      const params = new URLSearchParams(searchParams);
      params.set('inboundPage', '0');
      
      // Update sorts in URL
      updateSortsInURL(newSorts, params, 'inbound');
      
      // Update filters in URL
      updateFiltersInURL(newFilters, params, 'inbound');
      
      setSearchParams(params);
    },
    [inboundSorts, inboundFilters, searchParams, setSearchParams, updateSortsInURL, updateFiltersInURL]
  );

  // Handlers for outbound table
  const handleOutboundPageChange = (newPage: number) => {
    setOutboundPage(newPage);
    updateURLParams({ outboundPage: newPage });
  };

  const handleOutboundPageSizeChange = (newPageSize: number) => {
    setOutboundPageSize(newPageSize);
    setOutboundPage(0);
    updateURLParams({ outboundPage: 0, outboundPageSize: newPageSize });
  };

  const handleOutboundSort = useCallback(
    (column: string, direction: SortDirection | undefined, searchColumn?: string, searchTerm?: string) => {
      setOutboundPage(0);
      
      // Handle multiple sorts for outbound table
      let newSorts: SortConfig[] = [...outboundSorts];
      
      if (direction && direction !== 'undefined' as any) {
        // Check if this column is already in the sorts array
        const existingIndex = newSorts.findIndex((s) => s.column === column);
        
        if (existingIndex >= 0) {
          // Update existing sort direction
          newSorts[existingIndex].direction = direction;
        } else {
          // Add new sort
          newSorts.push({ column, direction });
        }
      } else {
        // Remove sort for this column if direction is undefined
        newSorts = newSorts.filter((s) => s.column !== column);
      }
      
      setOutboundSorts(newSorts);
      
      // Handle multiple filters for outbound table
      let newFilters: FilterConfig[] = [...outboundFilters];
      
      if (searchColumn !== undefined) {
        if (searchTerm && searchTerm.trim()) {
          // Check if this column already has a filter
          const existingFilterIndex = newFilters.findIndex((f) => f.column === searchColumn);
          
          if (existingFilterIndex >= 0) {
            // Update existing filter term
            newFilters[existingFilterIndex].term = searchTerm.trim();
          } else {
            // Add new filter
            newFilters.push({ column: searchColumn, term: searchTerm.trim() });
          }
        } else {
          // Remove filter for this column if searchTerm is empty or undefined
          newFilters = newFilters.filter((f) => f.column !== searchColumn);
        }
      }
      
      setOutboundFilters(newFilters);
      
      // Update legacy state for backward compatibility
      setOutboundSearchColumn(searchColumn);
      setOutboundSearchTerm(searchTerm);
      
      const params = new URLSearchParams(searchParams);
      params.set('outboundPage', '0');
      
      // Update sorts in URL
      updateSortsInURL(newSorts, params, 'outbound');
      
      // Update filters in URL
      updateFiltersInURL(newFilters, params, 'outbound');
      
      setSearchParams(params);
    },
    [outboundSorts, outboundFilters, searchParams, setSearchParams, updateSortsInURL, updateFiltersInURL]
  );

  // Comment modal handlers
  const handleOpenCommentModal = (fileId: number, isMarkingForRemoval: boolean = false) => {
    setCommentModal({
      isOpen: true,
      fileId,
      isMarkingForRemoval,
    });
  };

  const handleCloseCommentModal = () => {
    setCommentModal({
      isOpen: false,
      fileId: null,
      isMarkingForRemoval: false,
    });
  };

  const handleCommentSubmit = (comment: string) => {
    if (commentModal.fileId) {
      // Call delete API with file ID and comment (comment may be empty for non-FINAL files)
      deleteFile.mutate({
        fileId: commentModal.fileId,
        comment: comment.trim() || undefined,
      });

      // Close the modal
      handleCloseCommentModal();
    }
  };

  const handleCloseCommentWithTableModal = () => {
    setCommentWithTableModal({
      isOpen: false,
      selectedFile: null,
      tableData: [],
    });
  };

  const handleCommentWithTableSubmit = (comment: string) => {
    if (!commentWithTableModal.selectedFile) return;
    
    submitComment.mutate({
      dataSetId: Number(commentWithTableModal.selectedFile.fileId),
      comment
    });
  };

  const handleOpenCommentWithTableModal = async (file: e53File) => {
    try {
      // For now, we'll set empty comments array and let the component fetch them
      setCommentWithTableModal({
        isOpen: true,
        selectedFile: file,
        tableData: [],
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to load comments',
        severity: 'error',
      });
    }
  };

  // Handle context menu actions for all tables
  const handleContextMenuAction = useCallback(
    async (action: ContextMenuAction, row: e53File, tableType?: 'all' | 'Inbound' | 'Outbound') => {
      switch (action) {
        case 'ViewSamples':
          if (hasModulePermission('e53 View Samples')) {
            navigate(`/e53/samples/${row.fileId}`);
          } else {
            setSnackbar({
              open: true,
              message: 'You do not have permission to view samples',
              severity: 'error',
            });
          }
          break;
        case 'editSamples':
          if (hasModulePermission('e53 Edit Samples')) {
            // Validate if user can edit this file
            const validation = validateFileOperation(row, 'edit');
            
            if (validation.allowed) {
              navigate(`/e53/samples/${row.fileId}?editMode=true`);
            } else {
              setSnackbar({
                open: true,
                message: validation.message,
                severity: 'error',
              });
            }
          } else {
            setSnackbar({
              open: true,
              message: 'You do not have permission to edit samples',
              severity: 'error',
            });
          }
          break;
        case 'accept':
          if (hasModulePermission('e53 Edit Samples')) {
            // Always delegate validation to backend; show its message on error
            try {
              acceptFile(Number(row.fileId), {
                onSuccess: () => {
                  handleAcceptSuccess();
                },
                onError: (error) => {
                  handleAcceptError(error);
                },
              });
            } catch (error) {
              handleAcceptError(error);
            }
          } else {
            setSnackbar({
              open: true,
              message: 'You do not have permission to accept files',
              severity: 'error',
            });
          }
          break;
        case 'download':
          downloadFile(Number(row.fileId));
          break;
        case 'delete':
          if (hasModulePermission('Delete e53 files')) {
            // Validate if user can delete this file
            const validation = validateFileOperation(row, 'delete') as { allowed: boolean; title?: string; message: string; requiresComment?: boolean };
            
            if (validation.allowed) {
              // Show confirmation dialog
              setConfirmDialog({
                isOpen: true,
                title: validation.title || 'Confirm Deletion',
                message: validation.message,
                confirmAction: () => {
                  // Set the operation type for success message
                  setDeletionOperationType(validation.title || 'Draft File Removal');
                  
                  // If comment is required, open comment modal; otherwise delete directly
                  if (validation.requiresComment) {
                    // Check if this is a FINAL file not marked for removal (marking for removal)
                    const isFinal = row.fileStatus === 'F';
                    const isMarkedForRemoval = (row as any).inboundRemovalDate !== null || (row as any).outboundRemovalDate !== null || row.removalDate !== null;
                    handleOpenCommentModal(Number(row.fileId), isFinal && !isMarkedForRemoval);
                  } else {
                    // Delete without comment
                    deleteFile.mutate({
                      fileId: Number(row.fileId),
                      comment: undefined
                    });
                  }
                }
              });
            } else {
              setSnackbar({
                open: true,
                message: validation.message,
                severity: 'error',
              });
            }
          } else {
            setSnackbar({
              open: true,
              message: 'You do not have permission to delete files',
              severity: 'error',
            });
          }
          break;
        case 'comment':
          // Open CommentWithTable modal
          handleOpenCommentWithTableModal(row);
          break;
        case 'contact':
          // Open Contact modal to display counterpart emails
          setContactModal({
            isOpen: true,
            selectedFile: row,
          });
          break;
        case 'cn54Report':
          // Check if user has permission to view CN54 reports
          if (hasModulePermission('CN54 Report')) {
            // Build query parameters from row data
            const queryParams = new URLSearchParams();
            
            // Set PPO filter based on table type
            let ppoValue = 'All';
            if (tableType === 'Inbound') {
              // For inbound table, use the outbound PPO (who sent the file)
              ppoValue = row.operatorByOutboundPpo || 'All';
            } else if (tableType === 'Outbound') {
              // For outbound table, use the inbound PPO (who receives the file)
              ppoValue = row.operatorByInboundPpo || 'All';
            } else {
              // For 'all' table, try to determine based on available data
              ppoValue = row.operatorByOutboundPpo || row.operatorByInboundPpo || 'All';
            }
            queryParams.set('ppo', ppoValue);
            
            // Set period range based on the row's year and quarter
            if (row.accountingYear && row.accountingQuarter) {
              const fromYear = row.accountingYear;
              const toYear = row.accountingYear;
              const fromQuarter = row.accountingQuarter;
              const toQuarter = row.accountingQuarter;
              // Convert tableType to lowercase to match CN54Report expectations
              const table = (tableType || 'Inbound').toLowerCase();
              queryParams.set('fromYear', fromYear.toString());
              queryParams.set('toYear', toYear.toString());
              queryParams.set('fromQuarter', fromQuarter.toString());
              queryParams.set('toQuarter', toQuarter.toString());
              queryParams.set('table', table);
            }
            
            // Navigate to CN54 report with the constructed query parameters
            navigate(`/e53/reports/cn54?${queryParams.toString()}`);
          } else {
            setSnackbar({
              open: true,
              message: 'You do not have permission to view CN54 reports',
              severity: 'error',
            });
          }
          break;
        default:
          break;
      }
    },
    [navigate, downloadFile, hasModulePermission, userData, finalizeFile, validateFileOperation, handleAcceptSuccess, handleAcceptError, acceptFile]
  );

  // Generate column definitions using shared column definitions
  const allColumns = useMemo(() => getE53Columns({ userData, tableType: 'all' }), [userData]);
  const inboundColumns = useMemo(() => getE53Columns({ userData, tableType: 'inbound' }), [userData]);
  const outboundColumns = useMemo(() => getE53Columns({ userData, tableType: 'outbound' }), [userData]);

  const handleRowClick = (row: e53File) => {
  };

  // Define custom context menu actions for the tables
  const customContextMenuActions = useMemo(() => [
    { 
      action: 'download' as ContextMenuAction, 
      label: 'Download', 
      icon: <CloudDownload /> 
    },
    { 
      action: 'accept' as ContextMenuAction, 
      label: 'Accept', 
      icon: <Check /> 
    },
    { 
      action: 'delete' as ContextMenuAction, 
      label: 'Delete', 
      icon: <Delete /> 
    },
    { 
      action: 'ViewSamples' as ContextMenuAction, 
      label: 'View Samples', 
      icon: <Visibility /> 
    },
    { 
      action: 'editSamples' as ContextMenuAction, 
      label: 'Edit Samples', 
      icon: <Edit /> 
    },
    { 
      action: 'cn54Report' as ContextMenuAction, 
      label: 'CN54 Report', 
      icon: <BarChart /> 
    },
    { 
      action: 'comment' as ContextMenuAction, 
      label: 'Comment', 
      icon: <Comment /> 
    },
    { 
      action: 'contact' as ContextMenuAction, 
      label: 'Contact', 
      icon: <Email /> 
    },
    { 
      action: 'more' as ContextMenuAction, 
      label: 'More', 
      icon: <Help /> 
    },
  ], []);

  // Helper function to get the current table view from URL
  const getTableViewFromUrl = useCallback(() => {
    return (searchParams.get('type') as 'all' | 'inbound' | 'outbound') || 'all';
  }, [searchParams]);

  // Update tableView, filters, and pagination based on URL parameter changes
  useEffect(() => {
    const typeFilter = searchParams.get('type') || 'all';
    setTableView(typeFilter as 'all' | 'inbound' | 'outbound');

    // Extract state filter from search params (changed from 'status' to 'state')
    const stateFilter = searchParams.get('state') || 'all';
    setStatusView(stateFilter as 'all' | 'pending' | 'completed');

    const year = searchParams.get('year');
    if (!year || year === 'all') {
      setYearFilter(undefined);
    } else {
      setYearFilter(year);
    }

    const ppo = searchParams.get('ppo');
    if (!ppo || ppo === 'ALL') {
      setPpoFilter(undefined);
    } else {
      setPpoFilter(ppo);
    }

    // Get pagination and sorting parameters for main table
    const pendingPageParam = searchParams.get('pendingPage');
    const pendingPageSizeParam = searchParams.get('pendingPageSize');
    const pendingSearchColParam = searchParams.get('pendingSearchCol');
    const pendingSearchTermParam = searchParams.get('pendingSearchTerm');
    const parsedPendingSorts = parseSortsFromURL(searchParams, 'pending');
    const parsedPendingFilters = parseFiltersFromURL(searchParams, 'pending');

    // Get pagination and sorting parameters for inbound table
    const inboundPageParam = searchParams.get('inboundPage');
    const inboundPageSizeParam = searchParams.get('inboundPageSize');
    const inboundSearchColParam = searchParams.get('inboundSearchCol');
    const inboundSearchTermParam = searchParams.get('inboundSearchTerm');
    const parsedInboundSorts = parseSortsFromURL(searchParams, 'inbound');
    const parsedInboundFilters = parseFiltersFromURL(searchParams, 'inbound');

    // Get pagination and sorting parameters for outbound table
    const outboundPageParam = searchParams.get('outboundPage');
    const outboundPageSizeParam = searchParams.get('outboundPageSize');
    const outboundSearchColParam = searchParams.get('outboundSearchCol');
    const outboundSearchTermParam = searchParams.get('outboundSearchTerm');
    const parsedOutboundSorts = parseSortsFromURL(searchParams, 'outbound');
    const parsedOutboundFilters = parseFiltersFromURL(searchParams, 'outbound');

    // Update main table state
    if (pendingPageParam !== null) setPage(parseInt(pendingPageParam, 10));
    if (pendingPageSizeParam !== null) setPageSize(parseInt(pendingPageSizeParam, 10));
    const pendingSortsStr = JSON.stringify(parsedPendingSorts);
    const currentPendingSortsStr = JSON.stringify(pendingSorts);
    if (pendingSortsStr !== currentPendingSortsStr) {
      setPendingSorts(parsedPendingSorts);
    }
    const pendingFiltersStr = JSON.stringify(parsedPendingFilters);
    const currentPendingFiltersStr = JSON.stringify(pendingFilters);
    if (pendingFiltersStr !== currentPendingFiltersStr) {
      setPendingFilters(parsedPendingFilters);
    }
    if (pendingSearchColParam !== null) setSearchColumn(pendingSearchColParam);
    if (pendingSearchTermParam !== null) setSearchTerm(pendingSearchTermParam);

    // Update inbound table state
    if (inboundPageParam !== null) setInboundPage(parseInt(inboundPageParam, 10));
    if (inboundPageSizeParam !== null) setInboundPageSize(parseInt(inboundPageSizeParam, 10));
    const inboundSortsStr = JSON.stringify(parsedInboundSorts);
    const currentInboundSortsStr = JSON.stringify(inboundSorts);
    if (inboundSortsStr !== currentInboundSortsStr) {
      setInboundSorts(parsedInboundSorts);
    }
    const inboundFiltersStr = JSON.stringify(parsedInboundFilters);
    const currentInboundFiltersStr = JSON.stringify(inboundFilters);
    if (inboundFiltersStr !== currentInboundFiltersStr) {
      setInboundFilters(parsedInboundFilters);
    }
    if (inboundSearchColParam !== null) setInboundSearchColumn(inboundSearchColParam);
    if (inboundSearchTermParam !== null) setInboundSearchTerm(inboundSearchTermParam);

    // Update outbound table state
    if (outboundPageParam !== null) setOutboundPage(parseInt(outboundPageParam, 10));
    if (outboundPageSizeParam !== null) setOutboundPageSize(parseInt(outboundPageSizeParam, 10));
    const outboundSortsStr = JSON.stringify(parsedOutboundSorts);
    const currentOutboundSortsStr = JSON.stringify(outboundSorts);
    if (outboundSortsStr !== currentOutboundSortsStr) {
      setOutboundSorts(parsedOutboundSorts);
    }
    const outboundFiltersStr = JSON.stringify(parsedOutboundFilters);
    const currentOutboundFiltersStr = JSON.stringify(outboundFilters);
    if (outboundFiltersStr !== currentOutboundFiltersStr) {
      setOutboundFilters(parsedOutboundFilters);
    }
    if (outboundSearchColParam !== null) setOutboundSearchColumn(outboundSearchColParam);
    if (outboundSearchTermParam !== null) setOutboundSearchTerm(outboundSearchTermParam);

    // Reset pagination when filters change
    if (tableView !== getTableViewFromUrl()) {
      setPage(0);
      setInboundPage(0);
      setOutboundPage(0);

      const resetParams = new URLSearchParams(searchParams);
      resetParams.delete('pendingPage');
      resetParams.delete('inboundPage');
      resetParams.delete('outboundPage');
      setSearchParams(resetParams);
    }
  }, [searchParams, getTableViewFromUrl, setSearchParams, parseSortsFromURL, parseFiltersFromURL, pendingSorts, pendingFilters, inboundSorts, inboundFilters, outboundSorts, outboundFilters]);

  // Show loading indicator based on which tables are visible
  const showLoading =
    ((statusView === 'all' && tableView === 'all' || statusView === 'pending') && isPendingLoading) ||
    ((tableView === 'all' || tableView === 'inbound') && isInboundLoading) ||
    ((tableView === 'all' || tableView === 'outbound') && isOutboundLoading) ||
    permissionLoading ||
    userLoading;

  // Show error based on which tables are visible
  const showError =
    ((statusView === 'all' && tableView === 'all' || statusView === 'pending') && isPendingError) ||
    ((tableView === 'all' || tableView === 'inbound') && isInboundError) ||
    ((tableView === 'all' || tableView === 'outbound') && isOutboundError);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid="e53-message-container">
      <Grid container>
        <Grid item xs={12}>
          <SectionBar
            title="e53 Message"
            subtitle="e53 Sampling"
            module='e53'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            showFilters={{ state: true, type: true, year: true, ppo: true }}
            data-testid="e53-message-section-bar"
          />
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }} data-testid="e53-message-sidebar-container">
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="e53-message-sidebar" />
            </Box>
          )}

          <MainContent sidebarOpen={sidebarOpen} data-testid="e53-message-main-content">
            {showLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="e53-message-loading">
                <CircularProgress />
              </Box>
            ) : showError ? (
              <Alert severity="error" data-testid="e53-message-error">Error loading data</Alert>
            ) : !hasPermission ? (
              <Alert severity="warning" data-testid="e53-message-permission-alert">You don't have permission to view this section</Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: '100%', overflow: 'hidden' }} data-testid="e53-message-content">
                {((statusView === 'all' && tableView === 'all') || statusView === 'pending') && (
                  <TableSection data-testid="e53-message-pending-section">
                    <Table
                      data={pendingData?.content || []}
                      columns={allColumns}
                      page={page}
                      pageSize={pageSize}
                      todoColumn={true}
                      totalCount={pendingData?.totalElements}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                      moduleName="Pending Actions"
                      rowEventHandlers={{
                        onClick: handleRowClick,
                        onContextMenuAction: handleContextMenuAction,
                      }}
                      rowBackgroundColor={getRowBackgroundColor}
                      customActions={customContextMenuActions}
                      hideDefaultActions={true}
                      tableType="all"
                      onSort={handleSort}
                      defaultSort={pendingSorts.length > 0 ? pendingSorts[0] : undefined}
                      defaultSorts={pendingSorts.length > 0 ? pendingSorts : undefined}
                      defaultFilters={pendingFilters.length > 0 ? pendingFilters : undefined}
                      initialSearchColumn={searchColumn}
                      initialSearchTerm={searchTerm}
                      data-testid="e53-message-pending-table"
                    />
                  </TableSection>
                )}

                {(tableView === 'all' || tableView === 'inbound') && (
                  <TableSection data-testid="e53-message-inbound-section">
                    {isInboundLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="e53-message-inbound-loading">
                        <CircularProgress />
                      </Box>
                    ) : isInboundError ? (
                      <Alert severity="error" data-testid="e53-message-inbound-error">Error loading inbound data</Alert>
                    ) : (
                      <Table
                        data={inboundData?.content || []}
                        columns={inboundColumns}
                        page={inboundPage}
                        pageSize={inboundPageSize}
                        totalCount={inboundData?.totalElements}
                        todoColumn={true}
                        rowBackgroundColor="rgb(230, 245, 230)"
                        onPageChange={handleInboundPageChange}
                        onPageSizeChange={handleInboundPageSizeChange}
                        moduleName={`Inbound from ${ppoFilter && ppoFilter !== 'ALL' ? ppoFilter : '*'} to ${userData?.designatedOperator || ''}`}
                        rowEventHandlers={{
                          onClick: handleRowClick,
                          onContextMenuAction: handleContextMenuAction,
                        }}
                        customActions={customContextMenuActions}
                        hideDefaultActions={true}
                        tableType="Inbound"
                        onSort={handleInboundSort}
                        defaultSort={inboundSorts.length > 0 ? inboundSorts[0] : undefined}
                        defaultSorts={inboundSorts.length > 0 ? inboundSorts : undefined}
                        defaultFilters={inboundFilters.length > 0 ? inboundFilters : undefined}
                        initialSearchColumn={inboundSearchColumn}
                        initialSearchTerm={inboundSearchTerm}
                        data-testid="e53-message-inbound-table"
                      />
                    )}
                  </TableSection>
                )}

                {(tableView === 'all' || tableView === 'outbound') && (
                  <TableSection data-testid="e53-message-outbound-section">
                    {isOutboundLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="e53-message-outbound-loading">
                        <CircularProgress />
                      </Box>
                    ) : isOutboundError ? (
                      <Alert severity="error" data-testid="e53-message-outbound-error">Error loading outbound data</Alert>
                    ) : (
                      <Table
                        data={outboundData?.content || []}
                        columns={outboundColumns}
                        page={outboundPage}
                        pageSize={outboundPageSize}
                        totalCount={outboundData?.totalElements}
                        todoColumn={true}
                        rowBackgroundColor="rgb(255, 240, 240)"
                        onPageChange={handleOutboundPageChange}
                        onPageSizeChange={handleOutboundPageSizeChange}
                        moduleName={`Outbound from ${userData?.designatedOperator || ''} to ${ppoFilter && ppoFilter !== 'ALL' ? ppoFilter : '*'}`}
                        rowEventHandlers={{
                          onClick: handleRowClick,
                          onContextMenuAction: handleContextMenuAction,
                        }}
                        customActions={customContextMenuActions}
                        hideDefaultActions={true}
                        tableType="Outbound"
                        onSort={handleOutboundSort}
                        defaultSort={outboundSorts.length > 0 ? outboundSorts[0] : undefined}
                        defaultSorts={outboundSorts.length > 0 ? outboundSorts : undefined}
                        defaultFilters={outboundFilters.length > 0 ? outboundFilters : undefined}
                        initialSearchColumn={outboundSearchColumn}
                        initialSearchTerm={outboundSearchTerm}
                        data-testid="e53-message-outbound-table"
                      />
                    )}
                  </TableSection>
                )}
              </Box>
            )}

            <NotificationSnackbar
              open={snackbar.open}
              message={snackbar.message}
              severity={snackbar.severity}
              onClose={handleSnackbarClose}
              data-testid="e53-message-notification"
            />
          </MainContent>
        </Grid>
      </Grid>

      <Dialog
        open={confirmDialog.isOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        data-testid="e53-message-confirm-dialog"
      >
        <DialogTitle id="alert-dialog-title" data-testid="e53-message-confirm-dialog-title">{confirmDialog.title}</DialogTitle>
        <DialogContent data-testid="e53-message-confirm-dialog-content">
          <DialogContentText id="alert-dialog-description" data-testid="e53-message-confirm-dialog-text">
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions data-testid="e53-message-confirm-dialog-actions">
          <Button onClick={handleCloseConfirmDialog} color="primary" data-testid="e53-message-confirm-dialog-cancel">
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} color="primary" autoFocus data-testid="e53-message-confirm-dialog-ok">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <CommentModal
        isOpen={commentModal.isOpen}
        onClose={handleCloseCommentModal}
        onSubmit={handleCommentSubmit}
        title={commentModal.isMarkingForRemoval ? 'Mark File for Removal' : 'Delete E53 File'}
        description={commentModal.isMarkingForRemoval 
          ? 'Please provide a comment explaining why you are marking this file for removal.'
          : 'Please provide a comment explaining why you are deleting this file.'}
        submitText={commentModal.isMarkingForRemoval ? 'Mark for Removal' : 'Delete'}
        cancelText='Cancel'
        isLoading={deleteFile.isPending}
        data-testid="e53-message-comment-modal"
      />

      {/* CommentWithTable Modal */}
      <CommentWithTable
        isOpen={commentWithTableModal.isOpen}
        onClose={handleCloseCommentWithTableModal}
        onSubmit={handleCommentWithTableSubmit}
        title={`Comment on File ${commentWithTableModal.selectedFile?.fileId || ''}`}
        submitText='Submit Comment'
        cancelText='Cancel'
        tableData={commentsData?.comments || []}
        datasetInfo={commentsData ? {
          accountingYear: commentsData.accountingYear,
          accountingQuarter: commentsData.accountingQuarter,
          inboundRemovalDate: commentsData.inboundRemovalDate,
          outboundRemovalDate: commentsData.outboundRemovalDate,
        } : undefined}
        columns={[
          {
            Header: 'Date',
            accessor: 'date',
            width: '20%',
          },
          {
            Header: 'DO',
            accessor: 'ppo',
            width: '20%',
          },
          {
            Header: 'Version',
            accessor: 'version',
            width: '20%',
          },
          {
            Header: 'Comment',
            accessor: 'comment',
            width: '39%',
          }
        ]}
        commentRequired={true}
        commentLabel='Your Comment'
        commentPlaceholder='Enter your comment here...'
        maxWidth="lg"
        fullWidth={true}
        isLoading={submitComment.isPending}
        data-testid="e53-message-comment-with-table-modal"
      />

      {/* Contact Modal */}
      <ContactModal
        isOpen={contactModal.isOpen}
        onClose={() => setContactModal({ isOpen: false, selectedFile: null })}
        row={contactModal.selectedFile}
        moduleName="e53 Sampling"
      />
    </Box>
  );
};

export default e53Message; 